import React, { memo, useState } from 'react';
import { Td, Text, Tr } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useSelector } from 'react-redux';

import { getPlanPageThemeColors, PlanPageThemeColors } from 'redux/ThemeSlice';
import { getDataTypeBasedValuewithNewValue } from 'utils/FormatValues';

import {
  PLAN_TABLE_CELL_PX,
  PLAN_TABLE_CELL_PY,
  PLAN_TABLE_STICKY_BG,
  PLAN_TABLE_CELL_BORDER,
  PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
  PLAN_TEXT,
  neutral,
  text as textToken,
} from '../planPageRevampUi';

interface Properties {
  row: any;
  expandKey: string;
}

const formatCell = (row: any, item: any) => {
  if (item?.value === null) return row?.format_properties?.null_value ?? '-';
  return getDataTypeBasedValuewithNewValue(
    row?.data_format,
    item?.value || 0,
    row?.format_properties,
  );
};

const getCellBackground = (
  themeColors: PlanPageThemeColors,
  item: any,
  expandKey: string,
  isSubRow?: boolean,
) => {
  if (item?.isExpandedColumn) {
    return PLAN_TABLE_STICKY_BG;
  }
  if (item?.name === expandKey) {
    return isSubRow ? neutral.border : PLAN_TABLE_STICKY_BG;
  }
  if (item?.isActualValue) {
    return PLAN_TABLE_STICKY_BG;
  }
  return neutral.surface;
};

const PlanDataTableRow = ({ row, expandKey }: Properties) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const themeColors = useSelector(getPlanPageThemeColors);
  const hasSubRows = Boolean(row?.dim_item_data && row.dim_item_data.length > 0);

  const subRows = hasSubRows
    ? row.dim_item_data.map((item: any) => ({
        name: item?.name ?? '',
        values: item?.value || [],
      }))
    : [];

  const metricCellStyles = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 1,
    bg: PLAN_TABLE_STICKY_BG,
    py: PLAN_TABLE_CELL_PY,
    px: PLAN_TABLE_CELL_PX,
    minW: PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: PLAN_TABLE_CELL_BORDER,
    ...PLAN_TEXT.tableComfortaa,
    color: textToken.primary,
    whiteSpace: 'nowrap' as const,
  };

  const valueCellStyles = {
    py: PLAN_TABLE_CELL_PY,
    px: PLAN_TABLE_CELL_PX,
    ...PLAN_TEXT.tableComfortaaCell,
    color: textToken.primary,
    textAlign: 'end' as const,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: PLAN_TABLE_CELL_BORDER,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <>
      <Tr
        onClick={() => hasSubRows && setIsExpanded((previous) => !previous)}
        cursor={hasSubRows ? 'pointer' : 'default'}
        bg={neutral.surface}
        _hover={{ bg: neutral.surface }}
      >
        <Td {...metricCellStyles}>
          <Text as='span' display='inline-flex' alignItems='center' gap='10px'>
            {hasSubRows &&
              (isExpanded ? <ChevronDownIcon boxSize={3.5} /> : <ChevronRightIcon boxSize={3.5} />)}
            {row?.name}
          </Text>
        </Td>
        {(row?.total_values || []).map((item: any, index: number) => {
          if (!item?.isShow) return <React.Fragment key={`${item?.name ?? ''}-${index}`} />;
          return (
            <Td
              key={`${item?.name ?? ''}-${index}`}
              {...valueCellStyles}
              bg={getCellBackground(themeColors, item, expandKey)}
            >
              {formatCell(row, item)}
            </Td>
          );
        })}
      </Tr>

      {isExpanded &&
        subRows.map(
          (subRow: any, subRowIndex: number) =>
            subRow.values.length > 0 && (
              <Tr key={subRow.name + subRowIndex} bg={neutral.surface}>
                <Td
                  {...metricCellStyles}
                  pl={8}
                  {...PLAN_TEXT.tableComfortaaCell}
                  color={textToken.secondary}
                >
                  {subRow.name}
                </Td>
                {subRow.values.map((item: any, index: number) => {
                  if (!item?.isShow) return <React.Fragment key={`${item?.name ?? ''}-${index}`} />;
                  return (
                    <Td
                      key={`${item?.name ?? ''}-${index}`}
                      {...valueCellStyles}
                      color={textToken.secondary}
                      bg={getCellBackground(themeColors, item, expandKey, true)}
                    >
                      {formatCell(row, item)}
                    </Td>
                  );
                })}
              </Tr>
            ),
        )}
    </>
  );
};

export default memo(PlanDataTableRow);
